import React, { useEffect, useState } from 'react'
import { connect, useDispatch, useStore } from 'react-redux'
import {
  OneAuth as BaseOneAuth,
  OneAuthCallback,
  LogPayload,
  AuthPayload,
  InitialView,
  Options,
  ClientId,
} from '@chegg/one-auth'
import { UserRoles } from '@chegg-wt/core/constants/user'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { UserRole } from '@chegg-wt/core/types/auth'
import { AppRoute } from '@chegg-wt/core/types/core'
import { login } from '@chegg-wt/core/state/user/actions'
import { getRioConfig } from '@chegg-wt/core/state/instrumentation/rio/utils/rioConfig'
import {
  isAuthLoadingSelector,
  userRoleSelector,
} from '@chegg-wt/core/state/user/selectors'
import { AuthSuccessGrant } from '@chegg-wt/core/state/user/logics'
import {
  ClickstreamSignInSuccess1,
  ClickstreamSignUpSuccess1,
} from '@chegg/one-auth/dist/types/lib/rio/eventSchemas'
import { publicUrl } from '@chegg-wt/core/utils/url'
import { RefreshTokenQuery } from '@chegg/one-auth-sdk/dist/types/__generated__/auth-gate'
import { Config } from '@chegg-wt/core/config'
import { LoadingDots } from '../../LoadingDots'
import * as Styled from './styled'
import { getProfileCode } from './utils'

interface OneAuthProps {
  redirectTo?: AppRoute
  isLoading: boolean
  userRole: UserRole
  defaultFlow: InitialView
}

const OneAuth: React.FC<OneAuthProps> = ({
  redirectTo,
  userRole,
  isLoading,
  defaultFlow,
}) => {
  const dispatch = useDispatch()
  const { toProfile } = useRoutesContext()

  const state = useStore().getState()

  // Optimistically set to true as user is usually still fetching when page mounts.
  // If it turns out they are logged out then we will change value to false
  const [isAuthorizedOnEntry, setIsAuthorizedOnEntry] = useState(true)

  useEffect(() => {
    if (userRole && userRole === UserRoles.NOT_LOGGED_IN) {
      setIsAuthorizedOnEntry(false)
    } else if (userRole && isAuthorizedOnEntry) {
      navigated(toProfile())
    }
  }, [userRole])

  const callback: OneAuthCallback = (params) => {
    let auth_method
    const { type, payload } = params
    switch (type) {
      case 'log': {
        const { data, type } = payload as LogPayload

        switch (type) {
          case 'clickstream_sign_in_success':
            auth_method = (data as ClickstreamSignInSuccess1).sign_in.auth_service
            break
          case 'clickstream_sign_up_success':
            auth_method = (data as ClickstreamSignUpSuccess1).sign_up.auth_service
            break
          default:
        }
        break
      }
      case 'auth': {
        const { status, data, action } = payload as AuthPayload
        switch (status) {
          case 'success': {
            if (action === 'reset_password') break

            const successResponse: AuthSuccessGrant = {
              id_token: (data as RefreshTokenQuery['refreshToken']['tokens']).idToken,
              access_token: (data as RefreshTokenQuery['refreshToken']['tokens'])
                .accessToken,
              auth_method,
            }
            dispatch(login(successResponse, redirectTo))
            break
          }
          default: {
            break
          }
        }
        break
      }
      default: {
        break
      }
    }
  }

  // Passing empty view_name value as it will be modified by the OneAuth component
  // depending on the view, i.e., login, sign_up or reset_password
  const rioConfig = getRioConfig(state, '')
  const oneAuthOptions: Options = {
    footer: { image: publicUrl('/static/logo.svg') },
    signup: {
      form: {
        credentials: {
          ssoProviders: ['apple', 'facebook', 'google'],
        },
        userInfo: {
          student: {
            highschool: {
              highschoolGradYearSelect: true,
            },
            college: {
              collegeSelect: {
                yearInSchool: true,
              },
            },
          },
          parent: {},
        },
      },
    },
  }

  return (
    <Styled.OneAuthWrapper>
      {isLoading ? (
        <Styled.Loading>
          <LoadingDots />
        </Styled.Loading>
      ) : (
        <BaseOneAuth
          clientId={getProfileCode(Config.tokenSiteCode) as ClientId}
          callback={callback}
          rioConfig={rioConfig}
          view={defaultFlow || 'signin'}
          className="C-cheggauth one-auth-wrapper no-override"
          options={oneAuthOptions}
        />
      )}
    </Styled.OneAuthWrapper>
  )
}

const mapStateToProps = (state) => ({
  userRole: userRoleSelector(state),
  isLoading: isAuthLoadingSelector(state),
})

const mapDispatchToProps = {
  navigated,
}

export default connect(mapStateToProps, mapDispatchToProps)(OneAuth)
